"use client";

import { useEffect } from "react";
import { captureAnalyticsException } from "@/lib/analytics/posthog-client";
import { isTransientNetworkError } from "@/lib/virtualspace/transient-network-error";
import { useConnectionRetry } from "@/lib/virtualspace/use-connection-retry";

/**
 * Last-resort error boundary for errors thrown in the ROOT
 * LAYOUT itself (`app/layout.tsx`). When this fires, the normal
 * layout / providers / theme have NOT mounted, so we have to
 * render our own <html><body> and can't use any of our styled
 * components (no Tailwind classes from globals.css are
 * guaranteed to be in scope).
 *
 * This is the page users see only when the app is on fire. Keep
 * it readable, give them an obvious retry, include the digest.
 * The orbit/cube motif is self-contained and uses inline styles
 * so it still renders if the stylesheet itself failed to load.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // A dropped connection gets "Connection lost" and a Retry that refetches
  // the route; anything else keeps the crash copy.
  const transient = isTransientNetworkError(error);
  const retry = useConnectionRetry(reset);

  useEffect(() => {
    console.error("[global-error boundary]", error);
    captureAnalyticsException(
      error,
      transient ? { transient: true } : undefined,
    );
  }, [error, transient]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          boxSizing: "border-box",
          backgroundColor: "#000",
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px), radial-gradient(circle at 50% 35%, rgba(194,0,0,.18), transparent 32%)",
          backgroundSize: "48px 48px, 48px 48px, 100% 100%",
          color: "#f4f4f5",
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <main
          role="alert"
          style={{ width: "100%", maxWidth: 560, textAlign: "center" }}
        >
          <div
            style={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 160,
              height: 160,
              marginBottom: "1.25rem",
            }}
            aria-hidden="true"
          >
            <span
              style={{
                position: "absolute",
                inset: 4,
                border: "1px dashed rgba(255,255,255,.16)",
                borderRadius: "50%",
              }}
            />
            <span
              style={{
                position: "absolute",
                inset: 28,
                border: "1px solid rgba(255,255,255,.1)",
                borderRadius: "50%",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: 0,
                left: "50%",
                width: 8,
                height: 8,
                marginLeft: -4,
                borderRadius: "50%",
                background: "#ff3d3d",
                boxShadow: "0 0 14px #ff3d3d",
              }}
            />
            <span
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 80,
                height: 80,
                borderRadius: 16,
                border: "1px solid rgba(255,255,255,.15)",
                background: "rgba(0,0,0,.72)",
                boxShadow: "0 18px 60px rgba(0,0,0,.55)",
              }}
            >
              <img src="/logo-icon.png" alt="" width="56" height="56" />
            </span>
            <span
              style={{
                position: "absolute",
                right: 30,
                bottom: 31,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,.14)",
                background: "#0a0814",
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ff3d3d"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            </span>
          </div>
          <p
            style={{
              margin: "0 0 .75rem",
              color: "#ff6b6b",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
              fontSize: ".7rem",
              fontWeight: 600,
              letterSpacing: ".18em",
              textTransform: "uppercase",
            }}
          >
            {transient ? "Connection lost" : "Signal interrupted"}
          </p>
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(2rem, 7vw, 3rem)",
              fontWeight: 500,
              lineHeight: 1.08,
              letterSpacing: "-0.035em",
            }}
          >
            {transient ? "Connection lost." : "We drifted off course."}
          </h1>
          <p
            style={{
              maxWidth: 500,
              margin: "1rem auto 0",
              fontSize: "0.95rem",
              color: "#a1a1aa",
              lineHeight: 1.65,
            }}
          >
            {transient
              ? "We couldn't reach the server just now. Check your connection, then retry."
              : "Spatialist lost its bearings while loading. Try recalibrating; if it keeps happening, support can help you get back on course."}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: ".75rem",
              marginTop: "1.75rem",
            }}
          >
            <button
              onClick={transient ? retry : reset}
              style={{
                minHeight: 42,
                background: "#c20000",
                color: "white",
                border: "1px solid #c20000",
                borderRadius: 8,
                padding: "0.65rem 1.25rem",
                fontSize: "0.875rem",
                fontWeight: 650,
                cursor: "pointer",
              }}
            >
              {transient ? "Retry" : "Recalibrate"}
            </button>
            <a
              href="/"
              style={{
                minHeight: 42,
                boxSizing: "border-box",
                display: "inline-flex",
                alignItems: "center",
                color: "#f4f4f5",
                border: "1px solid rgba(255,255,255,.22)",
                borderRadius: 8,
                padding: "0.65rem 1.25rem",
                fontSize: "0.875rem",
                fontWeight: 650,
                textDecoration: "none",
              }}
            >
              Home base
            </a>
          </div>
          {error.digest && (
            <p
              style={{
                margin: "1.5rem auto 0",
                maxWidth: 480,
                overflowWrap: "anywhere",
                userSelect: "all",
                fontSize: "0.625rem",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
                letterSpacing: ".04em",
                color: "#52525b",
              }}
            >
              Support code · {error.digest}
            </p>
          )}
        </main>
      </body>
    </html>
  );
}
